import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("rig_token"));
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setUser(res.data))
        .catch(() => { localStorage.removeItem("rig_token"); setToken(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("rig_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const requestOtp = async (email) => {
    const res = await axios.post(`${API}/auth/otp/request`, { email });
    return res.data;
  };

  const verifyOtp = async (email, code) => {
    const res = await axios.post(`${API}/auth/otp/verify`, { email, code });
    localStorage.setItem("rig_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, role = "guest") => {
    const res = await axios.post(`${API}/auth/register`, { name, email, password, role });
    localStorage.setItem("rig_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("rig_token");
    setToken(null);
    setUser(null);
  };

  const api = useCallback((method, url, data = null) => {
    const config = { headers: authHeaders() };
    if (method === "get" || method === "delete") {
      return axios[method](`${API}${url}`, config);
    }
    return axios[method](`${API}${url}`, data, config);
  }, [authHeaders]);

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, api, requestOtp, verifyOtp,
      isAuthenticated: !!user,
      isAdmin: user?.role === "admin",
      isMember: user?.role === "member",
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
